'use client'

import {
  type InputHTMLAttributes,
  type ReactNode,
  type RefAttributes,
  useEffect,
  useRef,
} from 'react'

import { cn } from '@mntn-dev/ui-utilities'
import { mergeRefs } from '@mntn-dev/utilities'

import { forwardRef } from '../../utils/forward-ref.ts'
import type { TestIds } from '../../utils/testing.ts'
import {
  type UseFormFieldControlProps,
  useFormFieldControl,
} from '../form-field/use-form-field-control.ts'
import { getInputIconClassName, type InputSize } from './styles.ts'
import { useInputStyles } from './use-input-styles.ts'

type InputProps = Readonly<
  Omit<InputHTMLAttributes<HTMLInputElement>, 'readOnly' | 'size'> &
    RefAttributes<HTMLInputElement> &
    UseFormFieldControlProps<HTMLInputElement> &
    TestIds & {
      iconLeft?: ReactNode
      iconLeftInteractive?: boolean
      iconRight?: ReactNode
      iconRightInteractive?: boolean
      innerClassName?: string
      size?: InputSize
      value?: string
    }
>
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      autoComplete = 'off',
      autoFocus,
      className,
      dataTestId,
      dataTrackingId,
      disabled,
      hasError: hasErrorProp,
      hasSuccess: hasSuccessProp,
      hasWarning: hasWarningProp,
      iconLeft,
      iconLeftInteractive,
      iconRight,
      iconRightInteractive,
      id,
      onBlur,
      readOnly,
      size = 'md',
      ...inputProps
    },
    ref
  ) => {
    const innerRef = useRef<HTMLInputElement>(null)
    const mergedRef = mergeRefs(ref, innerRef)

    const { hasError, hasSuccess, hasWarning, ...fieldProps } =
      useFormFieldControl<HTMLInputElement>({
        disabled,
        id,
        hasError: hasErrorProp,
        hasSuccess: hasSuccessProp,
        hasWarning: hasWarningProp,
        dataTestId,
        dataTrackingId,
        onBlur,
        readOnly,
      })

    const { inputClassName, inputContainerClassName } = useInputStyles({
      disabled,
      hasError,
      hasLeftIcon: !!iconLeft,
      hasRightIcon: !!iconRight,
      hasSuccess,
      hasWarning,
      readOnly,
      size,
    })

    useEffect(() => {
      const preventScroll = (e: WheelEvent) => {
        if (e.target === innerRef.current) {
          e.preventDefault()
        }
      }

      if (autoFocus) {
        innerRef.current?.focus()
      }

      const inputInstance = innerRef.current
      if (inputInstance) {
        inputInstance.addEventListener('wheel', preventScroll)
      }

      return () => {
        if (inputInstance) {
          inputInstance.removeEventListener('wheel', preventScroll)
        }
      }
    }, [autoFocus])

    return (
      <div className={cn(inputContainerClassName, className)}>
        <input
          {...inputProps}
          {...fieldProps}
          className={cn(inputClassName, inputClassName)}
          ref={mergedRef}
        />
        {iconLeft && (
          <span className={getInputIconClassName('left', iconLeftInteractive)}>
            {iconLeft}
          </span>
        )}
        {iconRight && (
          <span
            className={getInputIconClassName('right', iconRightInteractive)}
          >
            {iconRight}
          </span>
        )}
      </div>
    )
  }
)

Input.id = 'Input'

export { Input, type InputProps }
